import { useMemo, useState, type ReactNode } from "react";
import { ArrowUpRight, BookmarkPlus, Bot, Check, Copy, FileText, Pencil, RotateCcw, ShieldAlert, ThumbsDown, ThumbsUp, WifiOff, X, Zap } from "lucide-react";
import type { AiProposal } from "@ncp/api-client";
import { formatRelative, useTranslation } from "@ncp/i18n";
import { AgentActivityFeed, Badge, Button, InlineAlert, MessageAction, MessageActions, Spinner, cn, stripWidgetMarkers, type AgentStepLike, notifySaved, notifySaveFailed } from "@ncp/ui";
import { useArtifact, useSaveMessage, type LiveTurn } from "../hooks";
import { ProposalList } from "../ProposalReview";
import { DataChartView, parseChartSpec } from "../DataViews";
import type { Artifact, HomeRun, ThreadDetail, ThreadMessage, ToolConfirmation } from "../types";
import { humanise } from "./labels";
import { DebugDetails, type TraceCall } from "./WorkSteps";
import { OpConfirmCard } from "./OpCard";
import { StagedLetters } from "./StagedLetters";

export type DecideFn = (c: ToolConfirmation, accept: boolean, edits?: Record<string, unknown>) => Promise<void>;

export function SystemNote({ children }: { children: ReactNode }) {
  return <p className="mx-auto max-w-xl rounded-lg bg-sand-100 px-3 py-1.5 text-center text-xs text-sand-700">{children}</p>;
}

export function traceOf(m: ThreadMessage): TraceCall[] {
  return m.toolCalls.map((c, i) => ({ id: `${m.id}-${i}`, tool: c.tool, agentKey: c.agentKey, phase: c.status === "Failed" || c.status === "Error" ? "error" : "end", summary: c.resultSummary ?? c.argsSummary, durationMs: c.durationMs }));
}

/** What an answer produced (proposals to review, charts, drafts) and any warnings, then the answer's actions. */
export function AnswerFooter({ message: m, threadId, detail, proposals, invalidate, compact, debug, onOpenArtifact, onOpenCase, onOpenRun, onFeedback, onRetry, readOnly = false }: {
  message: ThreadMessage;
  threadId: string;
  detail: ThreadDetail;
  proposals: AiProposal[];
  invalidate: readonly (readonly unknown[])[];
  compact: boolean;
  debug: boolean;
  onOpenArtifact?: (id: string) => void;
  onOpenCase?: (id: string) => void;
  onOpenRun?: (id: string) => void;
  onFeedback: (value: number) => void;
  /** Asks the same question again (offered on the latest answer). */
  onRetry?: () => void;
  /** A view-only account: no feedback and no saving the answer as a draft (both write). */
  readOnly?: boolean;
}) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  const save = useSaveMessage(threadId);
  const artifacts = detail.artifacts.filter((a) => m.artifactIds.includes(a.id));
  const savedKey = `answer-${m.id.replace(/-/g, "")}`;
  const saved = detail.artifacts.find((a) => a.key === savedKey);
  // Copy as Markdown: the answer as written, with the sources it cites listed under it ("[S1] Title — locator, p. 4"); a restricted
  // source is listed by its title only.
  const copy = async () => {
    try { await navigator.clipboard.writeText(answerMarkdown(m, t("ai.chat.sources", { defaultValue: "Sources" }))); setCopied(true); setTimeout(() => setCopied(false), 1500); } catch { /* clipboard unavailable */ }
  };
  const saveDraft = () => save.mutate(m.id, {
    onSuccess: (a) => notifySaved(a, onOpenArtifact ? { title: t("ai.chat.savedDraft"), action: { label: t("ai.chat.openDraft"), onClick: () => onOpenArtifact(a.id) } } : t("ai.chat.savedDraft")),
    onError: (e) => notifySaveFailed(e, t("ai.chat.saveFailed")),
  });
  return (
    <div className="mt-2 space-y-2">
      {proposals.length > 0 ? <div className="mt-3"><ProposalList proposals={proposals} invalidate={invalidate} defaultOpenSingle={false} compact onOpenRun={onOpenRun} onOpenCase={onOpenCase} /></div> : null}
      {artifacts.length > 0 ? <InlineCharts artifacts={artifacts} compact={compact} /> : null}
      {artifacts.length > 0 ? <ArtifactChips items={artifacts} onOpen={onOpenArtifact} /> : null}
      {m.guardrailFlags.length > 0 || m.offline || m.error ? (
        <div className="flex flex-wrap gap-1.5">
          {m.guardrailFlags.map((f) => /^ModelUnavailable.+Fallback$/.test(f)
            // An answer composed from the data tools without a model is not a guardrail finding: a neutral note says how it was made.
            ? <Badge key={f} size="sm" variant="outline" data-flag={f}><WifiOff /> {t(`ai.flags.${f}`, { defaultValue: t(`enums.guardrail.${f}`, { defaultValue: humanise(f) }) })}</Badge>
            : <Badge key={f} size="sm" variant="warning" data-flag={f}><ShieldAlert /> {t("ai.chat.guardrail", { flag: t(`ai.flags.${f}`, { defaultValue: t(`enums.guardrail.${f}`, { defaultValue: humanise(f) }) }) })}</Badge>)}
          {m.offline ? <Badge size="sm" variant="outline" data-offline-badge=""><WifiOff /> {t("ai.chat.offline")}</Badge> : null}
          {m.error ? <Badge size="sm" variant="danger">{m.error}</Badge> : null}
        </div>
      ) : null}
      {m.content ? (
        <MessageActions className="ea-actions" data-latest={onRetry ? "" : undefined}>
          <MessageAction label={copied ? t("ai.chat.copied") : t("ai.chat.copyMarkdown")} onClick={() => void copy()}>{copied ? <Check /> : <Copy />}</MessageAction>
          {onRetry ? <MessageAction label={t("ai.chat.regenerate")} onClick={onRetry}><RotateCcw /></MessageAction> : null}
          {readOnly ? null : (
            <>
              <MessageAction label={t("ai.chat.helpful")} aria-pressed={m.feedback === 1} className={cn(m.feedback === 1 && "bg-success-50 text-success-700")} onClick={() => onFeedback(1)}><ThumbsUp /></MessageAction>
              <MessageAction label={t("ai.chat.notHelpful")} aria-pressed={m.feedback === -1} className={cn(m.feedback === -1 && "bg-danger-50 text-danger-700")} onClick={() => onFeedback(-1)}><ThumbsDown /></MessageAction>
              {saved && onOpenArtifact
                ? <MessageAction label={t("ai.chat.savedDraft")} className="text-aegold-700" onClick={() => onOpenArtifact(saved.id)}><Check /></MessageAction>
                : <MessageAction label={t("ai.chat.saveDraft")} disabled={save.isPending} onClick={saveDraft}>{save.isPending ? <Spinner className="size-3.5" /> : <BookmarkPlus />}</MessageAction>}
            </>
          )}
          {m.runId && onOpenRun ? <Button size="sm" variant="ghost" className="h-7 px-2 text-[11px] text-sand-700" onClick={() => onOpenRun(m.runId!)}><Bot className="size-3.5" /> {t("ai.agentActivity")}</Button> : null}
        </MessageActions>
      ) : null}
      {debug ? <DebugDetails modelId={m.modelId} latencyMs={m.latencyMs} inputTokens={m.inputTokens} outputTokens={m.outputTokens} calls={traceOf(m)} /> : null}
    </div>
  );
}

/** An answer as Markdown for the clipboard: its text, then the sources its markers cite. */
export function answerMarkdown(m: Pick<ThreadMessage, "content" | "citations">, sourcesLabel: string): string {
  // The widgets a marker places are drawn on screen only: the clipboard gets the prose.
  const text = stripWidgetMarkers(m.content).trim();
  const cited = m.citations.filter((c) => text.includes(`[S${c.index}]`)).sort((a, b) => a.index - b.index);
  if (cited.length === 0) return text;
  const lines = cited.map((c) => {
    const where = [c.locator, !c.withheld && c.page ? `p. ${c.page}` : null].filter(Boolean).join(", ");
    return `- [S${c.index}] ${c.title}${where ? ` — ${where}` : ""}`;
  });
  return `${text}\n\n**${sourcesLabel}**\n\n${lines.join("\n")}\n`;
}

/** Charts read better drawn than as a chip; tables already appear in the answer's own text. */
export function InlineCharts({ artifacts, compact }: { artifacts: Artifact[]; compact: boolean }) {
  const charts = artifacts.filter((a) => a.kind === "Chart").slice(0, 2);
  if (charts.length === 0) return null;
  return <div className="space-y-2">{charts.map((a) => <InlineChart key={a.id} id={a.id} height={compact ? 200 : 260} />)}</div>;
}

function InlineChart({ id, height }: { id: string; height: number }) {
  const artifact = useArtifact(id);
  const chart = useMemo(() => parseChartSpec(artifact.data?.content), [artifact.data?.content]);
  if (!chart) return null;
  return <div className="rounded-xl border border-sand-200 bg-white p-3"><DataChartView chart={chart} height={height} showTitle /></div>;
}

export function ArtifactChips({ items, onOpen }: { items: Array<Pick<Artifact, "id" | "title" | "version" | "kind">>; onOpen?: (id: string) => void }) {
  const { t } = useTranslation();
  return (
    <div className="flex flex-wrap gap-1.5">
      {items.map((a) => (
        <button key={a.id} type="button" disabled={!onOpen} onClick={() => onOpen?.(a.id)} className="inline-flex max-w-full items-center gap-2 rounded-xl border border-sand-200 bg-white px-2.5 py-1.5 text-xs text-aeblack-900 shadow-[0_1px_2px_rgb(43_38_32/0.05)] transition hover:border-aegold-300 hover:bg-aegold-50/40 disabled:cursor-default">
          <span className="grid size-6 shrink-0 place-items-center rounded-md bg-seablue-50 text-seablue-800"><FileText className="size-3.5" /></span>
          <span className="truncate font-medium" dir="auto">{a.title}</span>
          <span className="shrink-0 text-[10px] text-muted-foreground">{t(`ai.chat.artifactKind.${a.kind}`, { defaultValue: a.kind })} · {t("ai.chat.artifactVersion", { version: a.version })}</span>
        </button>
      ))}
    </div>
  );
}

/** Visible card fields the user may reword before confirming; the server applies edits to these (never to the `_` targets). */
const EDITABLE_ARGS = new Set(["title", "summary", "note", "category"]);

const STATUS_TONE: Record<string, "success" | "danger" | "outline" | "warning"> = { Executed: "success", Failed: "danger", Declined: "outline", Expired: "outline", Confirmed: "warning" };

/** An action the assistant staged, waiting for the user: one primary button to confirm it, editing and cancelling beside it. */
export function ConfirmCard({ confirmation: c, locale, onDecide, onOpenLink, debug = false }: { confirmation: ToolConfirmation; locale: string; onDecide?: DecideFn; onOpenLink?: (link: string) => void; debug?: boolean }) {
  const { t } = useTranslation();
  const [busy, setBusy] = useState<"yes" | "no" | null>(null);
  const [editing, setEditing] = useState(false);
  const [edits, setEdits] = useState<Record<string, string>>({});
  const title = locale === "ar" && c.titleAr ? c.titleAr : c.title;
  const description = locale === "ar" && c.descriptionAr ? c.descriptionAr : c.description;
  // What a person reads: no internal ids (`_x`, `…Id`), and of a bilingual pair (`summary` / `summaryAr`) the reader's language only.
  const fields = Object.entries(c.arguments).filter(([k, v]) => !k.startsWith("_") && !/(Id|Ids)$/.test(k) && v !== null && v !== undefined && v !== ""
    && !(locale === "ar" ? !k.endsWith("Ar") && typeof c.arguments[`${k}Ar`] === "string" && c.arguments[`${k}Ar`] !== "" : k.endsWith("Ar") && k.length > 2 && c.arguments[k.slice(0, -2)] !== undefined));
  const editable = Object.entries(c.arguments).filter(([k, v]) => EDITABLE_ARGS.has(k) && (v === null || typeof v === "string"));
  const expired = c.status === "Pending" && new Date(c.expiresAt).getTime() <= Date.now();

  if (c.status !== "Pending" || expired) {
    const status = expired ? "Expired" : c.status;
    return (
      <div className="mt-2 flex min-w-0 items-center gap-2 rounded-xl border border-sand-200 bg-sand-50/70 px-3 py-2 text-xs" data-confirm-done="">
        <ShieldAlert className="size-3.5 shrink-0 text-sand-600" />
        <span className="min-w-0 flex-1 truncate font-medium text-aeblack-900" title={title}>{title}</span>
        <span className="flex shrink-0 items-center gap-1">
          <Badge size="sm" variant={STATUS_TONE[status] ?? "outline"}>{t(`ai.chat.confirmStatus.${status}`, { defaultValue: status })}</Badge>
          {status === "Executed" && c.result?.link && onOpenLink ? <Button size="sm" variant="ghost" className="h-6 px-1.5 text-[11px]" onClick={() => onOpenLink(c.result!.link!)}><ArrowUpRight className="size-3" /> {t("ai.chat.openResult")}</Button> : null}
        </span>
      </div>
    );
  }

  const run = async (accept: boolean) => {
    if (!onDecide) return;
    setBusy(accept ? "yes" : "no");
    const changed = Object.fromEntries(Object.entries(edits).filter(([k, v]) => v !== (c.arguments[k] ?? "")));
    try { await onDecide(c, accept, accept && Object.keys(changed).length > 0 ? changed : undefined); } finally { setBusy(null); }
  };
  return (
    <div className="mt-3 overflow-hidden rounded-2xl border border-aegold-200 bg-white shadow-[0_1px_2px_rgb(43_38_32/0.06)]">
      <div className="flex items-start gap-3 border-b border-aegold-100 bg-aegold-50/60 px-4 py-3">
        <span className="mt-0.5 grid size-8 shrink-0 place-items-center rounded-lg bg-white text-aegold-700 ring-1 ring-aegold-200"><ShieldAlert className="size-4" /></span>
        <div className="min-w-0 flex-1">
          <p className="text-[11px] font-semibold text-aegold-800">{t("ai.chat.confirmTitle")}{debug ? <span className="ms-1.5 font-mono font-normal text-sand-600">{c.toolKey}</span> : null}</p>
          <p className="mt-0.5 font-semibold leading-snug text-aeblack-950" dir="auto">{plain(title)}</p>
          {description ? <p className="mt-0.5 text-sm text-aeblack-800" dir="auto">{plain(description)}</p> : null}
        </div>
      </div>
      <div className="px-4 py-3 text-sm">
        {editing ? (
          <div className="space-y-2">
            {editable.map(([k, v]) => (
              <label key={k} className="block text-xs">
                <span className="mb-0.5 block font-medium text-aeblack-800">{t(`ai.chat.confirmArg.${k}`, { defaultValue: humanise(k) })}</span>
                {k === "summary" || k === "note"
                  ? <textarea dir="auto" className="w-full rounded-lg border border-sand-300 bg-white px-2 py-1.5 text-sm focus:border-aegold-400 focus:outline-none" rows={3} value={edits[k] ?? String(v ?? "")} onChange={(e) => setEdits((s) => ({ ...s, [k]: e.target.value }))} />
                  : <input dir="auto" className="w-full rounded-lg border border-sand-300 bg-white px-2 py-1.5 text-sm focus:border-aegold-400 focus:outline-none" value={edits[k] ?? String(v ?? "")} onChange={(e) => setEdits((s) => ({ ...s, [k]: e.target.value }))} />}
              </label>
            ))}
          </div>
        ) : fields.length > 0 ? (
          <dl className="grid gap-x-4 gap-y-1.5 text-xs sm:grid-cols-2">
            {fields.map(([k, v]) => {
              const shown = edits[k] ?? v;
              return (
                <div key={k} className={cn("min-w-0", isWide(shown) && "sm:col-span-2")}>
                  <dt className="text-[11px] text-muted-foreground">{t(`ai.chat.confirmArg.${argLabel(k)}`, { defaultValue: humanise(argLabel(k)) })}</dt>
                  <dd className="break-words text-aeblack-900" dir="auto">{renderValue(shown)}</dd>
                </div>
              );
            })}
          </dl>
        ) : null}
        <StagedLetters confirmation={c} className="mt-3 max-h-[50vh] overflow-y-auto" />
        <div className="mt-3 flex flex-wrap items-center gap-2">
          {onDecide ? (
            <>
              <Button size="sm" variant="gold" loading={busy === "yes"} disabled={busy !== null} onClick={() => void run(true)}><Check /> {t("ai.chat.confirm")}</Button>
              {editable.length > 0 ? <Button size="sm" variant="ghost" disabled={busy !== null} aria-pressed={editing} onClick={() => setEditing((e) => !e)}><Pencil /> {editing ? t("ai.chat.confirmDoneEditing") : t("ai.chat.confirmEdit")}</Button> : null}
              <Button size="sm" variant="ghost" className="text-sand-700" loading={busy === "no"} disabled={busy !== null} onClick={() => void run(false)}><X /> {t("ai.chat.decline")}</Button>
            </>
          ) : <span className="text-xs text-muted-foreground">{t("ai.chat.confirmPendingHint")}</span>}
          <span className="ms-auto text-[11px] text-muted-foreground">{t("ai.chat.confirmExpires", { when: formatRelative(c.expiresAt, locale as "en" | "ar") })}</span>
        </div>
      </div>
    </div>
  );
}

/** Under the outcome line of a decided action: the error, or a link to what it created. */
export function ConfirmationResult({ confirmation: c, onOpenLink }: { confirmation: ToolConfirmation; onOpenLink?: (link: string) => void }) {
  const { t } = useTranslation();
  const link = c.status === "Executed" ? c.result?.link : null;
  if (!link || !onOpenLink) return null;
  return (
    <div className="mt-2">
      <Button size="sm" variant="outline" onClick={() => onOpenLink(link)}>
        <ArrowUpRight /> {link.startsWith("/matters/") ? t("ai.chat.openMatter") : link.startsWith("/watchers") ? t("ai.chat.openWatcher") : t("ai.chat.openResult")}
      </Button>
    </div>
  );
}

/** A bilingual field (`summaryAr`) is labelled as its pair (`summary`): the reader sees one, in their language. */
const argLabel = (k: string) => (k.length > 2 && k.endsWith("Ar") ? k.slice(0, -2) : k);

function isWide(v: unknown): boolean {
  return (typeof v === "string" && v.length > 60) || (typeof v === "object" && v !== null);
}

/** Markdown emphasis the server puts around names (*CPI outlook*) reads as plain words on a card. */
const plain = (s: string) => s.replace(/\*\*([^*]+)\*\*|\*([^*]+)\*/g, (_m, a, b) => a ?? b);

function renderValue(v: unknown): ReactNode {
  if (typeof v === "string") return plain(v);
  if (Array.isArray(v)) return <ul className="list-disc ps-4">{v.map((x, i) => <li key={i}>{typeof x === "object" ? JSON.stringify(x) : String(x)}</li>)}</ul>;
  if (v && typeof v === "object") {
    return (
      <ul className="space-y-0.5">
        {Object.entries(v as Record<string, unknown>).filter(([, x]) => x !== null && x !== "").map(([k, x]) => <li key={k}><span className="text-muted-foreground">{humanise(k)}:</span> {typeof x === "object" ? JSON.stringify(x) : String(x)}</li>)}
      </ul>
    );
  }
  return String(v);
}

export function RunProgress({ run, steps, onOpenRun }: { run: HomeRun; steps: AgentStepLike[]; onOpenRun?: (id: string) => void }) {
  const { t } = useTranslation();
  return (
    <div>
      <p className="mb-1 flex items-center gap-2 text-xs text-muted-foreground"><Spinner className="size-3.5" /> {t("ai.chat.delegatedAsync", { agent: run.agentName })}</p>
      {steps.length > 0 ? <AgentActivityFeed compact steps={steps} running /> : null}
      {onOpenRun ? <Button size="sm" variant="ghost" className="mt-1 h-7" onClick={() => onOpenRun(run.id)}><Bot /> {t("ai.agentActivity")}</Button> : null}
    </div>
  );
}

/** What the turn being answered has produced so far: hand-offs to specialists, proposals, drafts, actions to confirm, and errors. */
export function LiveExtras({ live, stepsByRun, specialistName, onOpenArtifact, onOpenRun, onDecide, saved, locale, debug, onRetry, onDismiss, inspector = false }: {
  live: LiveTurn;
  stepsByRun: Record<string, AgentStepLike[]>;
  specialistName: (key: string, fallback?: string | null) => string;
  onOpenArtifact?: (id: string) => void;
  onOpenRun?: (id: string) => void;
  onDecide?: DecideFn;
  /** Confirmations already in the saved thread; they render there, not twice. */
  saved: ReadonlyMap<string, ToolConfirmation>;
  locale: string;
  debug: boolean;
  onRetry: () => void;
  onDismiss: () => void;
  /** The ECONIC inspector: actions to confirm as op cards, a failed turn as a quiet error box with Retry (APPLE-SPEC §6.3, §7 10.16). */
  inspector?: boolean;
}) {
  const { t } = useTranslation();
  return (
    <div className="space-y-2">
      {live.delegations.map((d, i) => (
        <div key={`${d.agentKey}-${i}`} className="mt-2 rounded-xl border border-seablue-200 bg-seablue-50/50 p-2.5 text-xs">
          <p className="flex items-center gap-1.5 font-medium text-seablue-900"><Bot className="size-3.5" /> {t("ai.chat.delegatedTo", { agent: specialistName(d.agentKey, d.agentName) })}</p>
          {d.async ? <p className="mt-0.5 text-seablue-800/80">{t("ai.chat.delegatedAsync", { agent: specialistName(d.agentKey, d.agentName) })}</p> : null}
          {d.runId && (stepsByRun[d.runId]?.length ?? 0) > 0 ? <AgentActivityFeed compact className="mt-2" steps={stepsByRun[d.runId]!} running={!stepsByRun[d.runId]!.some((s) => s.kind === "Completed" || s.kind === "Failed")} /> : null}
          {d.runId && onOpenRun ? <Button size="sm" variant="ghost" className="mt-1 h-7" onClick={() => onOpenRun(d.runId!)}>{t("ai.agentActivity")}</Button> : null}
        </div>
      ))}
      {live.proposals.length > 0 ? (
        <ul className="mt-2 space-y-1">
          {live.proposals.map((p) => (
            <li key={p.proposalId} className="flex items-center gap-2 rounded-lg border border-aegold-200 bg-aegold-50/60 px-2.5 py-1.5 text-xs">
              <Zap className="size-3.5 text-aegold-700" />
              <span className="min-w-0 flex-1 truncate font-medium" dir="auto">{p.title}</span>
              <Badge size="sm" variant="gold">{t("ai.chat.proposalsPending")}</Badge>
            </li>
          ))}
        </ul>
      ) : null}
      {live.artifacts.length > 0 ? <ArtifactChips items={live.artifacts.map((a) => ({ id: a.artifactId, title: a.title, version: a.version, kind: a.artifactKind }))} onOpen={onOpenArtifact} /> : null}
      {live.confirmations.filter((c) => !saved.has(c.confirmationId)).map((c) => {
        const confirmation: ToolConfirmation = { id: c.confirmationId, threadId: "", messageId: "", toolKey: c.tool, title: c.title, description: c.description, arguments: safeJson(c.argumentsJson), status: "Pending", expiresAt: c.expiresAt };
        return inspector
          ? <OpConfirmCard key={c.confirmationId} locale={locale} onDecide={onDecide} debug={debug} confirmation={confirmation} />
          : <ConfirmCard key={c.confirmationId} locale={locale} onDecide={onDecide} debug={debug} confirmation={confirmation} />;
      })}
      {live.final?.offline ? <p className="mt-2 flex items-center gap-1.5 text-[11px] text-warning-700"><WifiOff className="size-3" /> {t("ai.chat.offline")}</p> : null}
      {live.error && inspector ? (
        <div className="ea-errbox" role="alert">
          <span className="tx"><span className="tt">{t("ai.chat.error")}</span> {live.error}</span>
          <span className="acts">
            <button type="button" className="ec-btn plain sm" onClick={onDismiss}>{t("actions.close")}</button>
            <button type="button" className="ec-btn secondary sm" onClick={onRetry}>{t("ai.chat.retry")}</button>
          </span>
        </div>
      ) : live.error ? (
        <InlineAlert tone="danger" title={t("ai.chat.error")} className="mt-2">
          <p className="text-xs">{live.error}</p>
          <div className="mt-2 flex gap-2">
            <Button size="sm" variant="outline" onClick={onRetry}>{t("ai.chat.retry")}</Button>
            <Button size="sm" variant="ghost" onClick={onDismiss}>{t("actions.close")}</Button>
          </div>
        </InlineAlert>
      ) : null}
    </div>
  );
}

function safeJson(json: string): Record<string, unknown> {
  try { const v = JSON.parse(json); return v && typeof v === "object" ? (v as Record<string, unknown>) : {}; } catch { return {}; }
}
